import React from 'react';

/**
 * lazy chunk 加载失败兜底（2026-11，静态资源切 CDN 的配套）：
 * 路由切换时 dynamic import 的 chunk 从 CDN 加载失败（网络抖动 / CDN 故障）会抛错，
 * Suspense 不重试、又无 ErrorBoundary，页面会永久卡 Loader。本 boundary 只兜 chunk 类
 * 错误：渲染兜底 UI，并带 sessionStorage 一次性标记整页 reload（标记不清除——清了会
 * 造成「挂载即解防」的 reload 风暴）；失败发生在路由切换时、目标页面尚未渲染，无表单
 * 数据丢失风险。
 *
 * 非 chunk 错误（页面自身 render bug）在 render 中 rethrow 放行：boundary state 不随
 * 路由切换复位，吞掉会把一次瞬时 bug 变成整站会话级锁死；rethrow 后维持加 boundary
 * 前的未捕获行为（React 卸载树 + 浏览器 Uncaught 报告留有排障线索——react-dom 17
 * 生产构建会剥离自身日志，被 boundary 吞掉的错误才零痕迹）。
 *
 * 兜底 UI 零依赖（i18n 词典等资源未必可用，文案硬编码中英双语）。
 */
const CHUNK_ERROR_PATTERN = /Loading (CSS )?chunk \d+ failed|ChunkLoadError/;
const RELOAD_KEY = 'XJ_CHUNK_RELOAD';

const isChunkError = (error: Error): boolean => CHUNK_ERROR_PATTERN.test(error.message);

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

class ChunkErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {error: null};
  }

  static getDerivedStateFromError(error: Error): State {
    return {error};
  }

  componentDidCatch(error: Error): void {
    if (!isChunkError(error)) return;
    // storage 访问须守卫：禁用站点数据的浏览器（Chrome「阻止所有 Cookie」等）里
    // sessionStorage 访问器本身抛 SecurityError，不守卫会让自动 reload 在受限环境静默失效
    try {
      if (sessionStorage.getItem(RELOAD_KEY) === '1') return;
      sessionStorage.setItem(RELOAD_KEY, '1');
    } catch {
      return;
    }
    window.location.reload();
  }

  render(): React.ReactNode {
    const {children} = this.props;
    const {error} = this.state;
    if (!error) return children;
    if (!isChunkError(error)) throw error;
    return (
      <div style={{alignItems: 'center', display: 'flex', height: '50vh', justifyContent: 'center'}}>
        <div style={{color: '#64748b', fontSize: 14}}>
          页面加载失败，请
          {/* href 去掉 fragment：URL 带 hash 时点击与当前完全相同的链接是 same-document 导航（只滚动不重载），「刷新重试」会失效 */}
          <a href={`${window.location.href.split('#')[0]}`}>刷新重试</a> / Failed to load, please refresh.
        </div>
      </div>
    );
  }
}

export default ChunkErrorBoundary;
